import { useEffect, useState } from "react";
import {
    getLinkedinFunnel,
    getLinkedinFunnelStage,
    LinkedinFunnel,
    LinkedinFunnelPage,
    LinkedinStage,
} from "../../../interfaces/linkedinFunnel";

interface StageMeta {
    key: LinkedinStage;
    label: string;
    hint: string;
    color: string;
}

/**
 * Grouped by what you would do about them, not by raw status: the first block is the queue
 * moving on its own, the second is waiting on a decision, the third is settled.
 */
const PHASES: { title: string; hint: string; stages: StageMeta[] }[] = [
    {
        title: "En file",
        hint: "Avance tout seul, sans intervention",
        stages: [
            {
                key: "notSeeded",
                label: "Jamais interrogés",
                hint: "Aucun enrichissement LinkedIn — le cron leur en crée un (500 max par cycle)",
                color: "text-gray-700",
            },
            {
                key: "pending",
                label: "En attente de recherche",
                hint: "Prêts à partir au prochain cycle (*/5 min)",
                color: "text-blue-600",
            },
            {
                key: "processing",
                label: "Verrou pris",
                hint: "État transitoire — s'ils stagnent ici, le cycle s'est interrompu",
                color: "text-amber-600",
            },
            {
                key: "waitingCallback",
                label: "Chez DataForSEO",
                hint: "Tâche envoyée, en attente du callback — relancés après 2 h",
                color: "text-purple-600",
            },
        ],
    },
    {
        title: "À décider",
        hint: "Des candidats existent, reste à trancher lequel est le bon",
        stages: [
            {
                key: "doneUnscored",
                label: "À scorer",
                hint: "Profils trouvés, en attente du cron de scoring (*/3 min)",
                color: "text-indigo-600",
            },
            {
                key: "readyAutoApprove",
                label: "Prêts pour l'auto-approbation",
                hint: "Un candidat passe ville + nom — l'auto-approve les prendra",
                color: "text-teal-600",
            },
            {
                key: "needsManualReview",
                label: "À vérifier à la main",
                hint: "Aucun candidat ne passe les deux checks — vérification humaine",
                color: "text-orange-600",
            },
        ],
    },
    {
        title: "Terminés",
        hint: "Plus rien à faire, sauf reprise manuelle",
        stages: [
            {
                key: "approvedAuto",
                label: "Approuvés (auto)",
                hint: "Validés par le cron d'auto-approbation",
                color: "text-green-600",
            },
            {
                key: "approvedManual",
                label: "Approuvés (manuel)",
                hint: "Validés depuis le back-office",
                color: "text-green-700",
            },
            {
                key: "doneNoOptions",
                label: "Sans candidat retenu",
                hint: "Recherche faite, aucun profil n'a passé le filtre du nom",
                color: "text-gray-500",
            },
            {
                key: "error",
                label: "En erreur",
                hint: "Aucun profil trouvé — non rejoués, pour ne pas racheter la même réponse",
                color: "text-red-600",
            },
        ],
    },
];

const CHECK_LABELS: Record<string, string> = {
    cityMatched: "ville",
    nameMatched: "nom",
    ownerRole: "rôle",
    restaurantIndustry: "resto",
    franchiseMatched: "franchise",
};

function StageRow({
    meta,
    count,
    allTime,
    total,
    active,
    onClick,
}: {
    meta: StageMeta;
    count: number;
    /** Set only where a job ignores the window; then it, not count, is the headline. */
    allTime?: number;
    total: number;
    active: boolean;
    onClick: () => void;
}) {
    // The bar stays the stage's share of the window on every row, including the ones
    // headlining an all-time number — otherwise these two would be the only bars in the
    // panel measured against a different population, which is worse than no bar.
    const pct = total > 0 ? (count / total) * 100 : 0;
    return (
        <button
            onClick={onClick}
            className={`w-full text-left px-3 py-2.5 border rounded-sm transition-colors ${
                active ? "border-gray-900 bg-gray-50" : "border-gray-200 hover:border-gray-400"
            }`}
        >
            <div className="flex items-baseline justify-between gap-3">
                <span className="text-sm font-medium text-gray-900">{meta.label}</span>
                <span className={`text-lg font-semibold tabular-nums ${meta.color}`}>
                    {(allTime ?? count).toLocaleString("fr-FR")}
                </span>
            </div>
            <p className="text-xs text-gray-400 mt-0.5 leading-snug">{meta.hint}</p>
            {allTime !== undefined && (
                <p className="text-xs text-gray-500 mt-0.5 tabular-nums">
                    dont {count.toLocaleString("fr-FR")} dans la fenêtre
                </p>
            )}
            <div className="h-1 bg-gray-100 rounded-xs mt-2 overflow-hidden">
                <div className="h-full bg-gray-800" style={{ width: `${Math.min(100, pct)}%` }} />
            </div>
        </button>
    );
}

export default function LinkedinFunnelPanel() {
    const [funnel, setFunnel] = useState<LinkedinFunnel | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [openStage, setOpenStage] = useState<LinkedinStage | null>(null);
    const [stagePage, setStagePage] = useState<LinkedinFunnelPage | null>(null);
    const [stageLoading, setStageLoading] = useState(false);

    // Caught, not thrown: a failing request belongs in the panel as a message, not as a
    // runtime overlay over the whole back-office.
    const load = () => {
        setLoading(true);
        setError(null);
        getLinkedinFunnel()
            .then(setFunnel)
            .catch((e: any) => setError(e?.response?.data?.error ?? e?.message ?? "Erreur inconnue"))
            .finally(() => setLoading(false));
    };

    useEffect(load, []);

    // A stage headlining an all-time count must open the all-time list, or the row total
    // and the tile above it disagree.
    const scopeFor = (stage: LinkedinStage): "window" | "all" =>
        funnel?.allTime?.[stage] !== undefined ? "all" : "window";

    const openStageAt = (stage: LinkedinStage, page = 1) => {
        setOpenStage(stage);
        setStageLoading(true);
        setError(null);
        getLinkedinFunnelStage(stage, page, 50, scopeFor(stage))
            .then(setStagePage)
            .catch((e: any) => setError(e?.response?.data?.error ?? e?.message ?? "Erreur inconnue"))
            .finally(() => setStageLoading(false));
    };

    const toggleStage = (stage: LinkedinStage) => {
        if (openStage === stage) {
            setOpenStage(null);
            setStagePage(null);
            return;
        }
        openStageAt(stage, 1);
    };

    if (loading) {
        return <p className="text-sm text-gray-500">Chargement du funnel…</p>;
    }
    if (!funnel) {
        return (
            <div className="border border-red-200 bg-red-50 rounded-sm px-4 py-3">
                <p className="text-sm text-red-700">Funnel indisponible : {error ?? "réponse vide"}</p>
                <button
                    onClick={load}
                    className="mt-2 bg-black text-white text-xs px-3 py-1.5 rounded-xs hover:bg-gray-800"
                >
                    Réessayer
                </button>
            </div>
        );
    }

    const s = funnel.stages;
    const remaining =
        s.notSeeded + s.pending + s.processing + s.waitingCallback + s.doneUnscored + s.needsManualReview;
    const approved = s.approvedAuto + s.approvedManual;
    // Tolerates a back-end that predates allTime rather than rendering NaN over it.
    const allTime = funnel.allTime ?? {};
    const approvedAllTime =
        (allTime.approvedAuto ?? s.approvedAuto) + (allTime.approvedManual ?? s.approvedManual);

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 px-4 py-3 border border-gray-200 rounded-sm bg-gray-50">
                <div>
                    <p className="text-xs text-gray-500">Personnes dans la fenêtre</p>
                    <p className="text-xl font-semibold text-gray-900 tabular-nums">
                        {funnel.inWindow.toLocaleString("fr-FR")}
                    </p>
                </div>
                <div>
                    <p className="text-xs text-gray-500">Reste à traiter</p>
                    <p className="text-xl font-semibold text-blue-600 tabular-nums">
                        {remaining.toLocaleString("fr-FR")}
                    </p>
                </div>
                <div>
                    <p className="text-xs text-gray-500">Approuvés</p>
                    <p className="text-xl font-semibold text-green-600 tabular-nums">
                        {approvedAllTime.toLocaleString("fr-FR")}
                    </p>
                    <p className="text-xs text-gray-400 tabular-nums">
                        dont {approved.toLocaleString("fr-FR")} dans la fenêtre
                    </p>
                </div>
                <div className="ml-auto text-xs text-gray-500 leading-relaxed">
                    <p>
                        Fenêtre :{" "}
                        <span className="font-medium text-gray-700">
                            {funnel.windowDays === null ? "aucune limite" : `${funnel.windowDays} jours`}
                        </span>{" "}
                        · événements ou date d'ouverture
                    </p>
                    <p>
                        {funnel.nafExcluded.toLocaleString("fr-FR")} exclus par le filtre NAF ·{" "}
                        {funnel.unusableName.toLocaleString("fr-FR")} sans nom exploitable
                    </p>
                </div>
                <button
                    onClick={load}
                    className="bg-black text-white text-xs px-3 py-1.5 rounded-xs hover:bg-gray-800"
                >
                    Rafraîchir
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {PHASES.map((phase) => (
                    <div key={phase.title} className="border border-gray-200 rounded-sm p-4">
                        <p className="text-sm font-semibold text-gray-900">{phase.title}</p>
                        <p className="text-xs text-gray-400 mb-3">{phase.hint}</p>
                        <div className="space-y-2">
                            {phase.stages.map((meta) => (
                                <StageRow
                                    key={meta.key}
                                    meta={meta}
                                    count={s[meta.key] ?? 0}
                                    allTime={allTime[meta.key]}
                                    total={funnel.inWindow}
                                    active={openStage === meta.key}
                                    onClick={() => toggleStage(meta.key)}
                                />
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            {openStage && (
                <div className="border border-gray-200 rounded-sm">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                        <p className="text-sm font-semibold text-gray-900">
                            {PHASES.flatMap((p) => p.stages).find((m) => m.key === openStage)?.label}
                            {stagePage && (
                                <span className="ml-2 text-xs font-normal text-gray-500">
                                    {stagePage.total.toLocaleString("fr-FR")} personne(s)
                                </span>
                            )}
                            {scopeFor(openStage) === "all" && (
                                <span className="ml-2 text-xs font-normal text-gray-400">
                                    · toute la base, hors fenêtre compris
                                </span>
                            )}
                        </p>
                        <button
                            onClick={() => toggleStage(openStage)}
                            className="text-xs text-gray-500 hover:text-gray-800"
                        >
                            Fermer
                        </button>
                    </div>

                    {stageLoading && <p className="px-4 py-6 text-sm text-gray-500">Chargement…</p>}

                    {!stageLoading && error && (
                        <p className="px-4 py-6 text-sm text-red-700">Erreur : {error}</p>
                    )}

                    {!stageLoading && stagePage && stagePage.data.length === 0 && (
                        <p className="px-4 py-6 text-sm text-gray-500">Personne à cette étape.</p>
                    )}

                    {!stageLoading && stagePage && stagePage.data.length > 0 && (
                        <>
                            <div className="overflow-x-auto">
                                <table className="w-full text-xs">
                                    <thead>
                                        <tr className="text-gray-500 border-b border-gray-200">
                                            <th className="text-left font-medium px-4 py-2">Nom</th>
                                            <th className="text-left font-medium px-4 py-2">Statut</th>
                                            <th className="text-left font-medium px-4 py-2">Meilleur candidat</th>
                                            <th className="text-left font-medium px-4 py-2">Score</th>
                                            <th className="text-left font-medium px-4 py-2">Checks</th>
                                            <th className="text-left font-medium px-4 py-2">Dernier essai</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {stagePage.data.map((p) => (
                                            <tr key={p._id} className="border-b border-gray-100">
                                                <td className="px-4 py-2 text-gray-900">{p.name}</td>
                                                <td className="px-4 py-2 text-gray-600">{p.status ?? "—"}</td>
                                                <td className="px-4 py-2">
                                                    {p.topOption ? (
                                                        <a
                                                            href={p.topOption.url}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            className="text-blue-600 hover:underline"
                                                        >
                                                            {p.topOption.url.replace(
                                                                /^https?:\/\/(www\.)?linkedin\.com\/in\//,
                                                                ""
                                                            )}
                                                        </a>
                                                    ) : (
                                                        <span className="text-gray-400">
                                                            {p.error ?? "—"}
                                                        </span>
                                                    )}
                                                    {p.optionCount > 1 && (
                                                        <span className="ml-2 text-gray-400">
                                                            +{p.optionCount - 1}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-2 tabular-nums text-gray-700">
                                                    {p.topOption?.score ?? "—"}
                                                </td>
                                                <td className="px-4 py-2">
                                                    <div className="flex flex-wrap gap-1">
                                                        {p.topOption?.checks
                                                            ? Object.entries(p.topOption.checks)
                                                                  .filter(([, v]) => v)
                                                                  .map(([k]) => (
                                                                      <span
                                                                          key={k}
                                                                          className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded-xs"
                                                                      >
                                                                          {CHECK_LABELS[k] ?? k}
                                                                      </span>
                                                                  ))
                                                            : null}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-2 text-gray-500">
                                                    {p.lastRunAt
                                                        ? new Date(p.lastRunAt).toLocaleDateString("fr-FR")
                                                        : "—"}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {stagePage.totalPages > 1 && (
                                <div className="flex items-center justify-between px-4 py-3">
                                    <button
                                        disabled={stagePage.page <= 1}
                                        onClick={() => openStageAt(openStage, stagePage.page - 1)}
                                        className="text-xs px-3 py-1.5 border border-gray-200 rounded-xs disabled:opacity-40"
                                    >
                                        Précédent
                                    </button>
                                    <span className="text-xs text-gray-500">
                                        Page {stagePage.page} / {stagePage.totalPages}
                                    </span>
                                    <button
                                        disabled={stagePage.page >= stagePage.totalPages}
                                        onClick={() => openStageAt(openStage, stagePage.page + 1)}
                                        className="text-xs px-3 py-1.5 border border-gray-200 rounded-xs disabled:opacity-40"
                                    >
                                        Suivant
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
